import { Link as MuiLink, LinkProps as MuiLinkProps } from '@mui/material'
import { Route } from 'lib/routes'
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- C1Link wraps these components
import Link, { LinkProps } from 'next/link'
import { AnchorHTMLAttributes, FC, ReactNode, forwardRef } from 'react'

/**
 * Props for C1Link - type-safe Next.js Link without styling.
 * Includes standard anchor attributes (target, rel, etc.) that Next.js Link passes through.
 */
export interface C1LinkProps extends Omit<LinkProps, 'href'>, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> {
    href: Route
    children: ReactNode
}

/**
 * Type-safe Next.js Link component without MUI styling.
 *
 * This is the default link component - use it for navigation without
 * adding visual link styling (underline, color).
 *
 * @example
 * import { routes } from 'lib/routes'
 * import { C1Link } from 'components/core/links/C1Link'
 *
 * <C1Link href={routes.home}>
 *   <Stack direction="row">
 *     <Icon />
 *     <Typography>Home</Typography>
 *   </Stack>
 * </C1Link>
 */
export const C1Link = forwardRef<HTMLAnchorElement, C1LinkProps>(
    ({ href, children, ...props }, ref) => (
        <Link href={href} {...props} ref={ref}>{children}</Link>
    ),
)
C1Link.displayName = 'C1Link'

/**
 * Props for C1StyledLink - type-safe link with MUI styling.
 */
export interface C1StyledLinkProps extends Omit<MuiLinkProps, 'href' | 'component'> {
    href: Route
    children: ReactNode
}

/**
 * Type-safe Link component with MUI styling (underline, link color).
 *
 * Use when you want text to look like a traditional hyperlink.
 *
 * @example
 * import { routes } from 'lib/routes'
 * import { C1StyledLink } from 'components/core/links/C1Link'
 *
 * <C1StyledLink href={routes.users.detail(userId)}>View user</C1StyledLink>
 */
export const C1StyledLink: FC<C1StyledLinkProps> = ({ href, children, ...props }) =>
    <MuiLink component={Link} href={href} {...props}>{children}</MuiLink>

// Legacy aliases for migration
/** @deprecated Use C1Link instead */
export const C1LinkRaw = C1Link
/** @deprecated Use C1StyledLink instead */
export type C1LinkRawProps = C1LinkProps
